import React from "react";
import { ShieldCheck, Scale, Leaf, Heart, ArrowRight } from "lucide-react";

const Stitch = ({ className = "", color = "#2E7D32" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const features = [
  {
    icon: ShieldCheck,
    title: "100% Voluntary & Non-Profit",
    description: "Committed to serving society with transparency, no profit motive, and absolute integrity.",
  },
  {
    icon: Scale,
    title: "Registered Charitable Status",
    description: "Established on 18 September 2015, operating under a legally registered public trust deed.",
  },
  {
    icon: Leaf,
    title: "Eco-Agricultural Focus",
    description: "Pioneering organic farming education and sustainable environmental development in rural pockets.",
  },
  {
    icon: Heart,
    title: "Disadvantaged Groups First",
    description: "Our core programs prioritize women, rural youth, tribal populations, and minorities.",
  },
];

const WhyChooseUs = () => {
  return (
    <section id="why-choose-us" className="py-24 bg-[#F2ECDD] scroll-mt-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Column: Image with Frame & Badge */}
          <div className="relative">
            <div className="relative border border-[#0A355C]/15 p-3 bg-white">
              <div className="overflow-hidden">
                <img
                  src="/images/agriculture.jpg"
                  alt="Agricultural training initiative by MBFSDL"
                  className="w-full h-[450px] object-cover hover:scale-102 transition duration-500"
                />
              </div>
              <span className="absolute -top-[3px] -left-[3px] w-4 h-4 border-t-2 border-l-2 border-[#2E7D32]" />
              <span className="absolute -bottom-[3px] -right-[3px] w-4 h-4 border-b-2 border-r-2 border-[#2E7D32]" />
            </div>

            {/* Float Card */}
            <div
              className="absolute -bottom-6 -left-6 bg-[#0A355C] text-white p-6 max-w-[210px] shadow-xl"
              style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 16px 100%, 0 82%)" }}
            >
              <h4 className="mbf-display text-[26px] font-semibold text-[#E0A526] leading-none">100%</h4>
              <p className="mbf-mono text-[9.5px] tracking-wider uppercase text-blue-100/50 mt-2">Non-Political</p>
              <p className="mbf-body text-[12px] text-blue-100/75 mt-1 leading-relaxed">
                Serving everyone, regardless of background or beliefs.
              </p>
            </div>
          </div>

          {/* Right Column: Copy & Feature Grid */}
          <div>
            <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
              Why Partner With Us
            </span>

            <h2 className="mbf-display text-4xl font-semibold text-[#0A355C] mt-4 leading-tight tracking-tight">
              A Trusted Partner for Grassroots Development
            </h2>
            <Stitch className="w-24 h-2 mt-4" color="#2E7D32" />

            <p className="mbf-body mt-6 text-[#514a3d] text-[15px] leading-relaxed">
              MBFSDL functions with government departments, local industries, and developmental bodies to implement 
              practical solutions. Based in Vellore, Tamil Nadu, we provide a reliable, compliant, and highly committed 
              vehicle for CSR and philanthropic investment.
            </p>

            <div className="grid sm:grid-cols-2 gap-5 mt-10">
              {features.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="flex flex-col gap-3 p-5 rounded-none border border-[#0A355C]/10 bg-white/40 hover:bg-white hover:border-[#0A355C]/20 transition duration-300"
                  >
                    <div className="w-10 h-10 rounded-full border border-[#0A355C] flex items-center justify-center text-[#2E7D32] bg-white shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>

                    <div>
                      <h3 className="mbf-display font-semibold text-[#0A355C] text-[15px]">
                        {item.title}
                      </h3>

                      <p className="mbf-body text-[#6b6355] text-xs mt-1.5 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10">
              <a 
                href="#contact"
                className="mbf-body inline-flex items-center gap-2 bg-[#0A355C] hover:bg-[#082440] text-white px-8 py-4 rounded-none font-semibold tracking-wider text-xs uppercase transition shadow-md"
              >
                Join Hands With Us
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;